import {MANAGE_BUDGET_URL, UPGRADE_PLAN_URL} from '@github-ui/copilot-chat-helpers/copilot-chat-helpers'
import {CopilotPlan} from '@github-ui/copilot-chat-types'
import type {AgentQuotaSnapshot} from '@github-ui/agent-sessions-types/agent-quota-snapshot'

/**
 * Quota banner / disabled-input state machine. Shared by the CCA QuotaBanner and
 * the Fix-with-Copilot QuotaExceededDialog so their copy never drifts.
 *
 * Mirrors the CFI and CB/CE UX guides:
 *   - https://github.com/github/token-based-billing-notes/blob/main/docs/integrator-guides/cfi-ux-guidance.md
 *   - https://github.com/github/token-based-billing-notes/blob/main/docs/integrator-guides/tbb-client-integration-guide.md#commercial-plans-businessenterprise
 */
export type QuotaBannerState =
  | {kind: 'none'}
  | {
      kind: 'approachingLimit'
      percentUsed: number
      isIndividual: boolean
      canUpgradePlan: boolean
      overageMode: boolean
    }
  | {kind: 'cfiBlocked'; quotaResetDate?: string; canUpgradePlan: boolean}
  | {kind: 'cfiBudgetHit'; canUpgradePlan: boolean}
  | {kind: 'ceCbBlocked'}

const APPROACHING_LIMIT_THRESHOLD = 0.25
// At or below this remaining fraction, the banner escalates from `info` to `warning`.
const HIGH_WARNING_THRESHOLD = 0.1

export function getQuotaBannerState(snapshot: AgentQuotaSnapshot | undefined | null): QuotaBannerState {
  if (!snapshot) return {kind: 'none'}

  const plan = snapshot.copilot_plan
  const isIndividual = plan !== CopilotPlan.Business && plan !== CopilotPlan.Enterprise

  if (!snapshot.can_start_session) {
    if (!isIndividual) {
      return {kind: 'ceCbBlocked'}
    }

    // CFI users: differentiate "no overage allowed → enable additional usage"
    // from "overage allowed but budget cap hit → increase budget".
    //
    // `overage_permitted === false` is ambiguous (never-enabled vs. enabled-then-
    // exhausted), but both resolve to the same budget-settings page, so false or
    // a missing value (older servers) uses the cfiBlocked copy.
    if (
      snapshot.overage_permitted === true &&
      snapshot.is_paid_individual === true &&
      plan !== CopilotPlan.IndividualFree
    ) {
      return {
        kind: 'cfiBudgetHit',
        canUpgradePlan: snapshot.can_upgrade_plan,
      }
    }
    return {
      kind: 'cfiBlocked',
      quotaResetDate: snapshot.quota_reset_date,
      canUpgradePlan: snapshot.can_upgrade_plan,
    }
  }

  // percent_remaining is normalized to [0, 1]. Surface a dynamic "used X%" value
  // so the warning escalates as remaining drops (75% used at 25% remaining →
  // 99% used at 1% remaining).
  const remaining = snapshot.percent_remaining
  if (remaining >= 0 && remaining <= APPROACHING_LIMIT_THRESHOLD) {
    const percentUsed = Math.min(100, Math.max(0, Math.round((1 - remaining) * 100)))
    // overage_permitted only applies to CFI; CB/CE always see "included credits" copy.
    const overageMode =
      isIndividual &&
      snapshot.overage_permitted === true &&
      snapshot.is_paid_individual === true &&
      plan !== CopilotPlan.IndividualFree
    return {
      kind: 'approachingLimit',
      percentUsed,
      isIndividual,
      canUpgradePlan: snapshot.can_upgrade_plan,
      overageMode,
    }
  }

  return {kind: 'none'}
}

/**
 * Visible banner description for a given state, also reused for the screen-reader
 * announcement so spoken and visible copy stay in sync. Returns '' for `none`.
 */
export function getQuotaBannerDescription(state: QuotaBannerState): string {
  switch (state.kind) {
    case 'cfiBlocked': {
      const resetDate = formatQuotaResetDate(state.quotaResetDate)
      return resetDate
        ? `You don't have enough AI credits to start a session. Limit resets on ${resetDate}.`
        : "You don't have enough AI credits to start a session."
    }
    case 'cfiBudgetHit':
      return "You've run out of your budget for additional usage of AI credits."
    case 'ceCbBlocked':
      return "You don't have enough AI credits to start a session. Contact your administrator for more information."
    case 'approachingLimit':
      return state.overageMode
        ? `You have used ${state.percentUsed}% of your additional AI credit usage.`
        : `You have used ${state.percentUsed}% of your included AI credits.`
    case 'none':
    default:
      return ''
  }
}

export interface QuotaActionLink {
  label: string
  href: string
}

export interface QuotaBannerContent {
  title: string
  primaryAction?: QuotaActionLink
  upgradeAction?: QuotaActionLink
}

export function getQuotaBannerContent(
  state: QuotaBannerState,
  snapshot?: AgentQuotaSnapshot | null,
): QuotaBannerContent {
  const manageBudget = (label: string): QuotaActionLink => ({label, href: MANAGE_BUDGET_URL})
  // A legacy "individual" plan can also mean Free. Only explicit paid status
  // justifies a link to additional-usage budget settings.
  const canManageBudget = snapshot?.is_paid_individual === true

  switch (state.kind) {
    case 'cfiBlocked':
      return {
        title: 'Insufficient AI credits',
        primaryAction: canManageBudget ? manageBudget('Enable additional usage') : undefined,
      }
    case 'cfiBudgetHit':
      return {
        title: 'Additional usage budget reached',
        primaryAction: canManageBudget ? manageBudget('Increase budget') : undefined,
      }
    case 'ceCbBlocked':
      // CB/CE users are org-admin-managed, so no self-serve CTAs.
      return {title: 'Insufficient AI credits'}
    case 'approachingLimit': {
      // "Manage budget" becomes "Increase budget" once a CFI user is in overage.
      const primaryAction =
        state.isIndividual && canManageBudget
          ? manageBudget(state.overageMode ? 'Increase budget' : 'Manage budget')
          : undefined
      return {
        title: 'Usage limit warning',
        primaryAction,
        upgradeAction:
          state.isIndividual && state.canUpgradePlan ? {label: 'Upgrade plan', href: UPGRADE_PLAN_URL} : undefined,
      }
    }
    case 'none':
      return {title: ''}
    default:
      return assertNever(state)
  }
}

function assertNever(state: never): never {
  throw new Error(`Unhandled quota banner state: ${JSON.stringify(state)}`)
}

/**
 * Blocked states where the user cannot start a session, so callers announce them
 * assertively; the approachingLimit warning is announced politely instead.
 */
export function isBlockingQuotaState(state: QuotaBannerState): boolean {
  return state.kind === 'cfiBlocked' || state.kind === 'cfiBudgetHit' || state.kind === 'ceCbBlocked'
}

/** True once the approachingLimit banner should escalate from `info` to `warning`. */
export function isHighSeverityApproachingLimit(percentUsed: number): boolean {
  return percentUsed >= Math.round((1 - HIGH_WARNING_THRESHOLD) * 100)
}

/** Formats an ISO date (YYYY-MM-DD) as "Month D", or undefined if missing/invalid. */
export function formatQuotaResetDate(isoDate: string | undefined): string | undefined {
  if (!isoDate) return undefined
  // Parse as UTC to avoid a TZ-dependent off-by-one on date-only values.
  const parsed = new Date(`${isoDate}T00:00:00Z`)
  if (Number.isNaN(parsed.getTime())) return undefined
  return new Intl.DateTimeFormat(undefined, {month: 'long', day: 'numeric', timeZone: 'UTC'}).format(parsed)
}
